import { FilterTag } from '../constants/types';

export type LedgerRow = {
    date: string;
    type: string;
    amount: string;
    balance: string;
    description: string;
    tag: FilterTag;
};

export type LedgerColumnVisibility = {
    showDeposits: boolean;
    showPremium: boolean;
    showRefund: boolean;
};

export const getColumnVisibility = (tag: FilterTag): LedgerColumnVisibility => {
    const isAll = tag === FilterTag.ALL;
    return {
        showDeposits: isAll || tag === FilterTag.DEPOSITS,
        showPremium: isAll || tag === FilterTag.PREMIUM,
        showRefund: isAll || tag === FilterTag.WALLET_REFUND,
    };
};

export const getAmountVariant = (
    amount: string
): 'positive' | 'negative' | 'zero' => {
    const trimmed = amount.trim();
    if (trimmed === '0.0' || trimmed === '0') return 'zero';
    if (trimmed.startsWith('+')) return 'positive';
    return 'negative';
};

export const parseAmount = (amount: string): number => {
    const cleaned = amount.replace(/[₹,]/g, '').replace(/^\+/, '').trim();
    const num = parseFloat(cleaned);
    return Number.isNaN(num) ? 0 : Math.abs(num);
};

export type AggregatedDay = {
    date: string;
    balance: string;
    deposits: number;
    premium: number;
    refund: number;
};

export function aggregateRowsByDate(rows: LedgerRow[]): AggregatedDay[] {
    const byDate = rows.reduce<Record<string, AggregatedDay>>((acc, row) => {
        if (!acc[row.date]) {
            acc[row.date] = {
                date: row.date,
                balance: row.balance,
                deposits: 0,
                premium: 0,
                refund: 0,
            };
        }
        if (row.tag === FilterTag.DEPOSITS) {
            acc[row.date].deposits += parseAmount(row.amount);
        } else if (row.tag === FilterTag.PREMIUM) {
            acc[row.date].premium += parseAmount(row.amount);
        } else if (row.tag === FilterTag.WALLET_REFUND) {
            acc[row.date].refund += parseAmount(row.amount);
        }
        return acc;
    }, {});
    return Object.values(byDate);
}

export const formatAmount = (value: number): string => {
    if (value === 0) return '0';
    if (value > 0) {
        return `₹${value.toLocaleString('en-IN', {
            minimumFractionDigits: 2,
        })}`;
    }
    return '—';
};

export const formatGroupedBalance = (value: number): string =>
    `₹${value.toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;

export const getAmountForColumn = (row: LedgerRow, tag: FilterTag) => {
    if (row.tag !== tag) return '0.0';
    return row.amount;
};

export const getWalletRefundAmount = (row: LedgerRow) => {
    if (row.tag !== FilterTag.WALLET_REFUND) return '0.0';
    return row.amount;
};
